import React from 'react';
import { Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { LocationFocusButton } from './mapFocus';
import {
  type CitedLocation,
  LOCATION_ZOOM,
  locationsView,
} from './citedLocations';

const categoryLabel = (category?: string) =>
  category ? category.replace(/_/g, ' ') : null;

/**
 * Wraps a citation marker so clicking it lists the places the source names,
 * each with a map zoom.
 */
export default function CitedLocationsPopover({
  sourceName,
  locations,
  children,
}: {
  sourceName: string;
  locations: CitedLocation[];
  children: React.ReactNode;
}) {
  const { setIsMapVisible, zoomToLocation } = useUscgMapContext();
  const zoomToAll = () => {
    const view = locationsView(locations);
    if (!view) return;
    setIsMapVisible(true);
    void zoomToLocation(view);
  };

  return (
    <Popover>
      <PopoverTrigger
        nativeButton={false}
        render={<span className="tw:inline" />}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="dark tw:w-80 tw:gap-2 tw:text-foreground"
        side="top"
        data-testid="cited-locations-popover"
      >
        <PopoverHeader>
          <PopoverTitle className="tw:pb-0">{sourceName}</PopoverTitle>
          <PopoverDescription>
            {locations.length} place{locations.length === 1 ? '' : 's'} on the
            map
          </PopoverDescription>
        </PopoverHeader>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="tw:self-start"
          onClick={zoomToAll}
        >
          <Map className="tw:size-4" />
          Zoom to all
        </Button>
        <ul className="tw:m-0 tw:flex tw:max-h-64 tw:list-none tw:flex-col tw:overflow-y-auto tw:p-0">
          {locations.map((location, index) => (
            <li
              key={`${location.lat},${location.lon},${index}`}
              className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:py-0.5"
            >
              <span className="tw:min-w-0 tw:truncate">
                {location.name}
                {categoryLabel(location.category) ? (
                  <span className="tw:text-muted-foreground">
                    {' '}
                    · {categoryLabel(location.category)}
                  </span>
                ) : null}
              </span>
              <LocationFocusButton
                location={location}
                label={`Zoom to ${location.name}`}
                zoom={LOCATION_ZOOM}
              />
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
